import {
  resolveManualEngagement,
  type CalculatorPlatform,
  type ManualEngagementFieldStrings,
} from '@/lib/engagement/calculator';
import { formatEngagementPercent } from '@/lib/engagement/format';
import { quoteFromCosts, type QuoteInputs } from './campaign-tools';

const pounds = (value: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
const count = (value: number | null) =>
  value === null
    ? 'not supplied'
    : new Intl.NumberFormat('en-GB', { maximumSignificantDigits: 21 }).format(value);
const platformNames = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube' };

export function engagementExport(
  platform: CalculatorPlatform,
  fields: ManualEngagementFieldStrings,
): string | null {
  const { parsed, result, hasHeadline } = resolveManualEngagement(fields);
  if (!hasHeadline) return null;
  const audience = platform === 'youtube' ? 'Subscribers' : 'Followers';
  return [
    `${platformNames[platform]} engagement calculation`,
    `Engagement by views: ${formatEngagementPercent(result.post)}`,
    `Working: ${count(result.breakdown.totalEngagements)} ÷ ${count(parsed.views)} × 100`,
    '',
    `Average views: ${count(parsed.views)}`,
    ...(
      [
        'likes',
        'comments',
        'shares',
        ...(platform === 'instagram' ? (['saves'] as const) : []),
      ] as const
    ).map(
      (key) => `${key.charAt(0).toUpperCase()}${key.slice(1)}: ${count(result.breakdown[key])}`,
    ),
    `${audience}: ${count(parsed.followers)}`,
    ...(result.byFollowers !== null
      ? [
          `Older ${audience.toLowerCase()} comparison: ${formatEngagementPercent(result.byFollowers)}`,
        ]
      : []),
    '',
    'Reporting window: add before sharing. Use the same posts and averaging basis for all inputs.',
    'Source: figures entered manually; not verified against a connected account. Missing metrics are excluded, not treated as zero. Views are not reach.',
    'Method: https://www.owlscran.com/blog/how-we-calculate-engagement-rate',
    `Calculator: https://www.owlscran.com/${platform}-engagement-rate-calculator`,
  ].join('\n');
}

export function quoteExport(input: QuoteInputs): string | null {
  const result = quoteFromCosts(input);
  if (!result) return null;
  return [
    `Quote subtotal: ${pounds(result.total)}`,
    `Production: ${Number(input.hours)} hours × ${pounds(Number(input.hourlyRate))} = ${pounds(result.production)}`,
    `Publication fee: ${pounds(Number(input.publication))}`,
    `Expenses: ${pounds(Number(input.expenses))}`,
    `Usage fee: ${pounds(Number(input.usage))}`,
    `Exclusivity fee: ${pounds(Number(input.exclusivity))}`,
    '',
    'Tax: not included. State any applicable tax separately.',
    'Before sending: add deliverables, dates, revisions, usage scope and duration, exclusivity, payment terms and quote validity.',
    'Source: your chosen costs and fees. This subtotal is not a market rate recommendation.',
    'Rate card guide: https://www.owlscran.com/influencer-rate-card-guide',
  ].join('\n');
}
